import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi, ProjectApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    KpiCard,
    MetricHelpLabel,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function centreName(centreWithDomains) {
    const centre = centreWithDomains?.projectCentre ?? centreWithDomains;
    return centre?.name ?? centre?.slug ?? centre?.id ?? "-";
}

function centreId(centreWithDomains) {
    const centre = centreWithDomains?.projectCentre ?? centreWithDomains;
    return centre?.id;
}

function centreDomains(centreWithDomains) {
    return (centreWithDomains?.domains ?? []).map((domain) => domain.hostName).filter(Boolean).join(", ");
}

export default function ReportingCentresLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [centres, setCentres] = useState([]);
    const [performanceByCentre, setPerformanceByCentre] = useState([]);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const visibleCentres = useMemo(() => centres
        .filter((centre) => (centresAllowListed ?? []).length === 0 || centresAllowListed.includes(centreId(centre)))
        .sort((a, b) => centreName(a).localeCompare(centreName(b), "cs")), [centres, centreFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setCentres([]);
        setPerformanceByCentre([]);

        const authorityConfiguration = getAuthorityConfiguration();
        const projectApi = new ProjectApi(authorityConfiguration);
        const erpApi = new ErpCalculationApi(authorityConfiguration);

        Promise.all([
            projectApi.listProjectCentres({projectId: project.id}),
            erpApi.shopPerformanceByCentre({
                projectId: project.id,
                erpFilter: {
                    since: sinceString,
                    until: untilString,
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            }),
        ])
            .then(([nextCentres, dimensionData]) => {
                if (!active) {
                    return;
                }

                setCentres(nextCentres);
                const centresById = new Map((nextCentres ?? []).map((centre) => [centreId(centre), centre]));
                setPerformanceByCentre((dimensionData?.rows ?? []).map((row) => ({
                    centre: centresById.get(row.id) ?? {id: row.id, name: row.name},
                    id: row.id,
                    name: row.name,
                    performance: row.performance,
                })));
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey]);

    const rows = useMemo(() => performanceByCentre
        .map(({centre, performance}) => {
            const turnover = performance?.orderTurnoverGross;
            const profit = performance?.orderProfitGross;
            const hasProfit = profit != null;
            const marketing = performance?.orderSpendMarketing;

            return {
                id: centreId(centre),
                name: centreName(centre),
                domains: centreDomains(centre),
                turnover,
                ...(hasProfit ? {profit} : {}),
                marketing,
                orders: Number(performance?.orderAmountGross ?? 0),
                completedOrders: Number(performance?.orderAmountNet ?? 0),
                ...(hasProfit ? {margin: dividePercent(moneyAmount(profit), moneyAmount(turnover))} : {}),
                pno: dividePercent(moneyAmount(marketing), moneyAmount(turnover)),
            };
        })
        .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover)), [performanceByCentre]);

    const currency = rows.find((row) => row.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = rows.reduce((sum, row) => sum + moneyAmount(row.turnover), 0);
    const totalProfit = rows.reduce((sum, row) => sum + moneyAmount(row.profit), 0);
    const hasProfit = rows.some((row) => row.profit != null);
    const totalOrders = rows.reduce((sum, row) => sum + row.orders, 0);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const turnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...rows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 180, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: [...rows].reverse().map((row, index) => ({
                    value: moneyToMajor(row.turnover),
                    itemStyle: {color: ChannelMixColors[(rows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
            },
        ],
    };

    const shareOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: rows.map((row) => ({
                    name: row.name,
                    value: moneyToMajor(row.turnover),
                })),
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.centres", {defaultValue: "Střediska"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.centres.title", {defaultValue: "K čemu slouží přehled středisek"})}
                >
                    {t("reporting_lab.descriptions.centres.text", {defaultValue: "Rozpadá výkon podle středisek projektu tam, kde má projekt více obchodních větví, například B2B/B2C nebo lokální střediska."})}
                </ReportingPageDescription>

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.centres.centres", {defaultValue: "Střediska"})} value={renderNumber(visibleCentres.length)} meta={visibleCentres.length <= 1 ? t("reporting_lab.centres.single_centre", {defaultValue: "Projekt má jedno středisko"}) : t("reporting_lab.centres.multiple_centres", {defaultValue: "Projekt má více středisek"})}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} value={renderCurrencyValue({amount: totalTurnover, currency}, true)} meta={t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"})}/>
                </div>
                {hasProfit && <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.profit", {defaultValue: "Marže"})} value={renderCurrencyValue({amount: totalProfit, currency}, true)} meta={renderPercentValue(dividePercent(totalProfit, totalTurnover))} tone={totalProfit < 0 ? "bad" : "default"}/>
                </div>}
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})} value={renderNumber(totalOrders)} meta={t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"})}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.centres.revenue_by_centre", {defaultValue: "Obrat podle střediska"})} className="col-span-12 xl:col-span-7">
                    <EChart option={turnoverOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.centres.revenue_share", {defaultValue: "Podíl středisek na obratu"})} className="col-span-12 xl:col-span-5">
                    <EChart option={shareOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.centres.table", {defaultValue: "Přehled středisek"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.centres.centre", {defaultValue: "Středisko"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.pages.domains", {defaultValue: "Domény"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                {hasProfit && <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.table.contribution", {defaultValue: "Marže"})} tooltip={t("reporting_lab.table.contribution_help", {defaultValue: "Marže = obrat přiřazených objednávek minus nákupní cena zboží a marketingové náklady. Nejde o čistý účetní zisk; nezahrnuje interní náklady dopravy, plateb ani režii."})}/></th>}
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.pno", {defaultValue: "PNO"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.orders.completed", {defaultValue: "Dokončené"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {rows.map((row) => <tr key={row.id}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.name}</td>
                                <td className="max-w-md py-3 pr-4 text-slate-600 dark:text-slate-300">{row.domains || "-"}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                {hasProfit && <td className={`py-3 pr-4 text-right ${moneyAmount(row.profit) < 0 ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>{renderCurrencyValue(row.profit, true)}</td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.pno)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.orders)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.completedOrders)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {rows.length === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.centres.empty", {defaultValue: "Pro vybrané období nejsou dostupná data středisek."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
